import React from 'react';

/**
 * ArchiveModeBanner — calm, premium read-only-mode notice.
 *
 * Shown when the workspace subscription has expired but the user is still
 * inside the 30-day grace window. The tone is deliberately non-alarming:
 * taupe + Playfair, never red, never shouty. Renders nothing when the
 * workspace is active.
 *
 * Expects the `archive_mode` block returned by GET /auth/settings:
 *   {
 *     active: bool,
 *     expired_at: iso string | null,
 *     pdf_limit_per_board: number,
 *     zip_limit_per_board: number,
 *     library_bulk_blocked: bool,
 *   }
 *
 * Quotas are PER BOARD — a studio with N boards can grab a final copy of
 * each one. The banner advertises the policy; actual per-board usage is
 * surfaced in the BoardEditor next to the export buttons.
 */
const ArchiveModeBanner = ({ archive, compact = false }) => {
  if (!archive || !archive.active) return null;

  const pdfLimit = archive.pdf_limit_per_board ?? 3;
  const zipLimit = archive.zip_limit_per_board ?? 1;

  return (
    <div
      data-testid="archive-mode-banner"
      style={{
        background: '#f6f1e8',
        border: '1px solid #d9c9a8',
        padding: compact ? '12px 18px' : '18px 22px',
        marginBottom: 22,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 18,
        flexWrap: 'wrap',
      }}
    >
      <div style={{ flex: '1 1 280px', minWidth: 0 }}>
        <p
          style={{
            margin: 0,
            fontFamily: 'Playfair Display, serif',
            fontSize: 11,
            letterSpacing: '0.28em',
            textTransform: 'uppercase',
            color: '#7a6a4a',
          }}
        >
          Archive Mode
        </p>
        <p
          style={{
            margin: '6px 0 0',
            fontFamily: 'Playfair Display, serif',
            fontSize: 17,
            lineHeight: 1.45,
            color: 'var(--kahla-charcoal)',
            fontWeight: 400,
          }}
        >
          Your workspace is paused. You can still view and export your work —
          uploads, edits, and new boards are on hold until you renew.
        </p>
      </div>

      <div
        data-testid="archive-mode-quota"
        style={{
          display: 'flex',
          gap: 22,
          flexShrink: 0,
          paddingTop: 4,
        }}
      >
        <QuotaPolicy label="PDF exports" allowance={pdfLimit} />
        <QuotaPolicy label="ZIP exports" allowance={zipLimit} />
      </div>
    </div>
  );
};

const QuotaPolicy = ({ label, allowance }) => (
  <div style={{ textAlign: 'left' }}>
    <p
      style={{
        margin: 0,
        fontFamily: 'Playfair Display, serif',
        fontSize: 10,
        letterSpacing: '0.22em',
        textTransform: 'uppercase',
        color: '#9b8a6c',
      }}
    >
      {label}
    </p>
    <p
      style={{
        margin: '4px 0 0',
        fontFamily: 'Playfair Display, serif',
        fontSize: 18,
        letterSpacing: '0.04em',
        color: 'var(--kahla-charcoal)',
      }}
    >
      {allowance}
      <span
        style={{
          fontSize: 11,
          color: '#9b8a6c',
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          marginLeft: 6,
          fontFamily: 'inherit',
        }}
      >
        per board
      </span>
    </p>
  </div>
);

export default ArchiveModeBanner;
